import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { GenrePage, GenreSort, VodKind } from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors, layout } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { PosterTile } from '../components/PosterTile.js';
import { scrollFocusedIntoView } from '../scrollFocusedIntoView.js';

/**
 * La pagina di un genere (03-SCHERMATE-TV, SPEC §3.7): **griglia verticale**, non una fascia.
 *
 * ## Perché non è più una riga
 *
 * Perché una riga orizzontale su una categoria da duecento titoli è un corridoio: per arrivare al
 * centesimo si preme ottanta volte a destra, e in nessun momento si ha idea di quanti siano. La
 * griglia scorre in verticale, mostra cinque titoli per riga, e la prima cosa che si legge è
 * **quanti sono**.
 *
 * ## Quante colonne lo decide la larghezza, e si misura
 *
 * Non una costante: la stessa app gira su un televisore a 1920 e in una finestra di browser. Si
 * misura il contenitore e si dividono le piastrelle — è quel che il disegno chiama «righe definite
 * dalla larghezza».
 *
 * **Con `getBoundingClientRect` e l'evento `resize`, non con `ResizeObserver`**: quello arriva in
 * Chromium 64 e i Tizen del 2017 sono fermi al 63. È lo stesso vincolo che vieta `globalThis` e
 * `\p{L}`, e la rete che lo tiene fermo è `tizen5.test.ts`.
 *
 * ## Il cursore è (riga, colonna) e le fasce di comandi sono righe
 *
 * Percorso, sottocategorie e ordinamenti stanno **dentro** il conteggio come righe 0, 1 e 2, per la
 * stessa ragione per cui le pastiglie dei generi stanno nella vetrina: un elemento che si vede e che
 * il telecomando non raggiunge è il difetto peggiore su un televisore — non c'è un puntatore con cui
 * aggirarlo.
 */

/** Quante colonne quando non si è ancora misurato niente: quelle di un televisore a 1920. */
export const GENRE_COLUMNS_FALLBACK = 5;

/** Lo spazio fra una piastrella e l'altra, orizzontale. Serve al conto delle colonne. */
const COLUMN_GAP = 24;

export interface VodGenreScreenProps {
  readonly focusPort: FocusPort;
  readonly kind: VodKind;
  readonly page: GenrePage;
  readonly sort: GenreSort;
  readonly unwatchedOnly: boolean;
  readonly onSort: (sort: GenreSort) => void;
  readonly onToggleUnwatched: () => void;
  /** Scende in una sottocategoria: chi sta sopra accoda l'identificativo al percorso. */
  readonly onOpenChild: (categoryId: string) => void;
  /** Una voce del percorso: `[]` significa «torna alla vetrina». */
  readonly onOpenPath: (path: ReadonlyArray<string>) => void;
  readonly onOpen: (contentId: string) => void;
  /** Indietro: risale di un livello, oppure esce quando si è al primo. */
  readonly onBack: () => void;
  readonly onRequestMenu: () => void;
  readonly menuHasFocus: boolean;
}

interface Cursor {
  readonly row: number;
  readonly col: number;
}

export function VodGenreScreen({
  focusPort,
  kind,
  page,
  sort,
  unwatchedOnly,
  onSort,
  onToggleUnwatched,
  onOpenChild,
  onOpenPath,
  onOpen,
  onBack,
  onRequestMenu,
  menuHasFocus,
}: VodGenreScreenProps): ReactNode {
  const translator = useTranslator();
  const [cursor, setCursor] = useState<Cursor>({ row: 0, col: 0 });

  // Come nelle altre schermate: le funzioni si leggono da un riferimento, perché elencarle fra le
  // dipendenze disferebbe e rifarebbe la sottoscrizione al telecomando a ogni disegno.
  const handlers = useRef({ onSort, onToggleUnwatched, onOpenChild, onOpenPath, onOpen, onBack, onRequestMenu });
  useEffect(() => {
    handlers.current = { onSort, onToggleUnwatched, onOpenChild, onOpenPath, onOpen, onBack, onRequestMenu };
  }, [onSort, onToggleUnwatched, onOpenChild, onOpenPath, onOpen, onBack, onRequestMenu]);

  const gridRef = useRef<HTMLDivElement | null>(null);
  const [columns, setColumns] = useState(GENRE_COLUMNS_FALLBACK);

  useLayoutEffect(() => {
    const measure = (): void => {
      const width = gridRef.current?.getBoundingClientRect().width ?? 0;
      if (width <= 0) return;
      const fit = Math.floor((width + COLUMN_GAP) / (layout.posterTileWidth + COLUMN_GAP));
      setColumns(Math.max(1, fit));
    };
    measure();
    if (typeof window === 'undefined') return undefined;
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  /*
    Cambiando nodo il cursore torna in cima.

    Restando dov'era, chi scende in una sottocategoria da tre titoli partendo dalla dodicesima riga
    si ritroverebbe davanti a niente — e la prima cosa che vede non sarebbe la prima cosa dell'elenco.
  */
  const nodeId = page.node?.id ?? '';
  useEffect(() => {
    setCursor({ row: 0, col: 0 });
  }, [nodeId, sort, unwatchedOnly]);

  const hasChildren = page.children.length > 0;

  /** Quante colonne ha ogni riga navigabile, comandi compresi. È tutto ciò che serve per muoversi. */
  const widths = useMemo(() => {
    const gridRows: number[] = [];
    for (let at = 0; at < page.items.length; at += columns) {
      gridRows.push(Math.min(columns, page.items.length - at));
    }
    return [
      page.crumbs.length,
      ...(hasChildren ? [page.children.length] : []),
      // Gli ordinamenti più l'interruttore «solo non visti», che è l'ultima colonna della fila.
      GENRE_SORT_ORDER.length + 1,
      ...gridRows,
    ];
  }, [page, columns, hasChildren]);

  /** Quale riga è la griglia: dopo percorso, (sottocategorie) e ordinamenti. */
  const firstGridRow = hasChildren ? 3 : 2;

  const move = useCallback(
    (deltaRow: number, deltaCol: number) => {
      setCursor((current) => {
        if (deltaRow !== 0) {
          // Si salta la riga vuota invece di atterrarci: un fuoco invisibile è un telecomando che
          // smette di rispondere.
          let row = current.row;
          for (let step = 0; step < widths.length; step += 1) {
            row += deltaRow;
            if (row < 0 || row >= widths.length) return current;
            if ((widths[row] ?? 0) > 0) {
              return { row, col: Math.min(current.col, (widths[row] ?? 1) - 1) };
            }
          }
          return current;
        }
        const available = widths[current.row] ?? 0;
        const col = current.col + deltaCol;
        if (col < 0 || col >= available) return current;
        return { row: current.row, col };
      });
    },
    [widths],
  );

  const choose = useCallback(() => {
    const api = handlers.current;

    if (cursor.row === 0) {
      const crumb = page.crumbs[cursor.col];
      if (crumb) api.onOpenPath(crumb.path);
      return;
    }

    if (hasChildren && cursor.row === 1) {
      const child = page.children[cursor.col];
      if (child) api.onOpenChild(child.id);
      return;
    }

    if (cursor.row === firstGridRow - 1) {
      if (cursor.col < GENRE_SORT_ORDER.length) {
        const chosen = GENRE_SORT_ORDER[cursor.col];
        if (chosen) api.onSort(chosen);
        return;
      }
      api.onToggleUnwatched();
      return;
    }

    const index = (cursor.row - firstGridRow) * columns + cursor.col;
    const entry = page.items[index];
    if (entry) api.onOpen(entry.item.id);
  }, [cursor, page, hasChildren, firstGridRow, columns]);

  useEffect(() => {
    if (menuHasFocus) return undefined;
    return focusPort.subscribe((key) => {
      if (key.kind === 'back') {
        handlers.current.onBack();
        return true;
      }
      if (key.kind === 'select') {
        choose();
        return true;
      }
      if (key.kind !== 'direction') return false;

      // A sinistra dalla prima colonna si va al menu: è la stessa uscita di tutte le altre schermate.
      if (key.direction === 'left' && cursor.col === 0) {
        handlers.current.onRequestMenu();
        return true;
      }
      if (key.direction === 'left') move(0, -1);
      if (key.direction === 'right') move(0, 1);
      if (key.direction === 'up') move(-1, 0);
      if (key.direction === 'down') move(1, 0);
      return true;
    });
  }, [focusPort, menuHasFocus, move, choose, cursor.col]);

  const frameRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const focused = frameRef.current?.querySelector<HTMLElement>('[data-focused="1"]');
    scrollFocusedIntoView(focused);
  }, [cursor, page]);

  const sectionTitle = translator.t(kind === 'movie' ? 'vod.movies.title' : 'vod.series.title');

  /*
    Il nodo non c'è più: il catalogo è cambiato sotto i piedi — una categoria tolta, un import
    rifatto. Si dice, e si lascia la via d'uscita: portare chi guarda in un posto che non ha chiesto
    sarebbe peggio.
  */
  if (page.node === null) {
    return (
      <div
        data-vod-genre="1"
        style={{ flex: 1, minWidth: 0, padding: '56px 72px', display: 'flex', flexDirection: 'column', gap: 16 }}
      >
        <span style={{ font: `700 22px ${FONT_STACK}`, color: colors.accent, letterSpacing: '.1em', textTransform: 'uppercase' }}>
          {sectionTitle}
        </span>
        <p data-vod-genre-gone="1" style={{ font: `600 30px ${FONT_STACK}`, color: colors.textDim, margin: 0 }}>
          {translator.t('vod.genre.gone')}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={frameRef}
      data-vod-genre="1"
      style={{
        flex: 1,
        minWidth: 0,
        overflowY: 'auto',
        scrollbarWidth: 'none',
        padding: '56px 72px 80px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 22,
      }}
    >
      {/* Il percorso: ogni voce riporta a quel livello, e «Film» torna alla vetrina. */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        {page.crumbs.map((crumb, index) => (
          <span key={`${crumb.id}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              data-f="1"
              data-crumb={crumb.id === '' ? 'root' : crumb.id}
              data-focused={cursor.row === 0 && cursor.col === index ? '1' : '0'}
              onClick={() => handlers.current.onOpenPath(crumb.path)}
              style={{
                flex: 'none',
                border: 0,
                margin: 0,
                height: 48,
                padding: '0 16px',
                borderRadius: 10,
                cursor: 'pointer',
                background: cursor.row === 0 && cursor.col === index ? colors.surfaceRaised : 'transparent',
                color: colors.accent,
                font: `700 22px ${FONT_STACK}`,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                outline: cursor.row === 0 && cursor.col === index ? `4px solid ${colors.accent}` : 'none',
                outlineOffset: 2,
              }}
            >
              {crumb.name}
            </button>
            <span style={{ font: `700 24px ${FONT_STACK}`, color: colors.textFaint }}>›</span>
          </span>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h1 style={{ font: `800 64px ${FONT_STACK}`, letterSpacing: '-.02em', margin: 0 }}>{page.node.name}</h1>
        <span data-vod-genre-count="1" style={{ font: `500 24px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t('vod.titles', { count: page.total })}
        </span>
      </div>

      {hasChildren && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span style={sectionLabelStyle}>{translator.t('vod.genre.subcategories')}</span>
          <div style={{ display: 'flex', gap: 12, overflowX: 'auto', scrollbarWidth: 'none', padding: '8px 8px 10px', margin: '0 -8px' }}>
            {page.children.map((child, index) => (
              <Chip
                key={child.id}
                testAttribute="data-subcategory"
                testValue={child.id}
                /* Il «›» dice che sotto c'è ancora qualcosa: senza, si scende alla cieca. */
                label={`${child.name} · ${child.count}${child.hasChildren ? ' ›' : ''}`}
                selected={false}
                focused={hasChildren && cursor.row === 1 && cursor.col === index}
                onSelect={() => handlers.current.onOpenChild(child.id)}
              />
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {GENRE_SORT_ORDER.map((value, index) => (
          <Chip
            key={value}
            testAttribute="data-sort"
            testValue={value}
            label={translator.t(`vod.genre.sort.${value}`)}
            selected={sort === value}
            focused={cursor.row === firstGridRow - 1 && cursor.col === index}
            onSelect={() => handlers.current.onSort(value)}
          />
        ))}
        <span style={{ width: 2, height: 36, background: colors.border, margin: '0 8px' }} />
        <Chip
          testAttribute="data-unwatched"
          testValue="1"
          label={
            unwatchedOnly
              ? `✓ ${translator.t('vod.genre.unwatchedOnly')}`
              : translator.t('vod.genre.unwatchedOnly')
          }
          selected={unwatchedOnly}
          accent
          focused={cursor.row === firstGridRow - 1 && cursor.col === GENRE_SORT_ORDER.length}
          onSelect={() => handlers.current.onToggleUnwatched()}
        />
      </div>

      <div
        ref={gridRef}
        data-vod-genre-grid="1"
        style={{
          paddingTop: 12,
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, ${layout.posterTileWidth}px)`,
          justifyContent: 'space-between',
          gap: `36px ${COLUMN_GAP}px`,
        }}
      >
        {page.items.map((entry, index) => (
          <PosterTile
            key={entry.item.id}
            title={entry.item.title}
            subtitle={subtitleOf(entry.item.genre, entry.item.year)}
            posterUrl={entry.item.posterUrl}
            percent={entry.state.percent}
            watched={entry.state.watched}
            lockedRating={null}
            focused={
              cursor.row === firstGridRow + Math.floor(index / columns) && cursor.col === index % columns
            }
            onSelect={() => handlers.current.onOpen(entry.item.id)}
          />
        ))}
      </div>

      {/*
        Zero titoli **con il filtro acceso** non è un genere vuoto, ed è il caso in cui si finisce
        davvero: si accende «solo non visti» su una categoria già finita e la pagina si svuota. Dirlo
        è la differenza fra un filtro e un guasto.
      */}
      {page.items.length === 0 && (
        <p data-vod-genre-empty="1" style={{ font: `600 28px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t(unwatchedOnly ? 'vod.genre.allWatched' : 'vod.genre.empty')}
        </p>
      )}
    </div>
  );
}

/** L'ordine in cui le pastiglie di ordinamento compaiono, che è anche quello del cursore. */
const GENRE_SORT_ORDER: ReadonlyArray<GenreSort> = ['newest', 'alphabetical'];

function subtitleOf(genre: string, year: number): string {
  return [genre.trim(), year > 0 ? String(year) : ''].filter((part) => part !== '').join(' · ');
}

function Chip({
  label,
  selected,
  focused,
  onSelect,
  testAttribute,
  testValue,
  accent = false,
}: {
  label: string;
  selected: boolean;
  focused: boolean;
  onSelect: () => void;
  testAttribute: string;
  testValue: string;
  accent?: boolean;
}): ReactNode {
  return (
    <button
      type="button"
      data-f="1"
      {...{ [testAttribute]: testValue }}
      data-selected={selected ? '1' : '0'}
      data-focused={focused ? '1' : '0'}
      onClick={onSelect}
      style={{
        flex: 'none',
        border: 0,
        margin: 0,
        height: 60,
        padding: '0 28px',
        borderRadius: 30,
        cursor: 'pointer',
        background: selected ? (accent ? colors.accent : colors.text) : colors.surfaceRaised,
        color: selected ? colors.background : colors.textMuted,
        font: `600 23px ${FONT_STACK}`,
        outline: focused ? `4px solid ${colors.accent}` : 'none',
        outlineOffset: 4,
        transform: focused ? 'scale(var(--fs, 1.04))' : 'none',
        transition: 'var(--tr, transform .16s ease)',
      }}
    >
      {label}
    </button>
  );
}

const sectionLabelStyle = {
  font: `600 20px ${FONT_STACK}`,
  color: colors.textDim,
  letterSpacing: '.06em',
  textTransform: 'uppercase' as const,
};
